import type {
    CounterState,
    DetectedEvent,
    GameScore,
    LiveScoreState,
    SchemaDisplayMap,
    SchemaTurnRotation,
    SchemaUnit,
    ScoreEvent,
    ScoreSchema,
    UnitScore,
    EngineInitialState,
} from './types'

// Faithful TypeScript port of SLL.Api/Services/Scoring/ScoreEngine.cs — the same replay
// spec runs on device (this file), on the server (C#), and in Flutter (Dart). The server
// replay is authoritative at finalisation; drift here surfaces as a clean finalisation
// mismatch, never corrupted points. Keep the two implementations in lockstep: the vitest
// suite mirrors ScoreEngineTests.cs scenario-for-scenario.

interface ActiveUnit {
    name: string
    unit: SchemaUnit
    index: number
    home: number
    away: number
    startHome: number // handicap preset this instance began from (serve maths subtract it)
    startAway: number
    childrenCompleted: number
    holderAtStart: string
}

const opposite = (side: string): string => (side === 'home' ? 'away' : 'home')

// a side that OWES points (handicap) sits below zero: "Owe 15" until it reaches love
const mappedLabel = (map: SchemaDisplayMap, raw: number, maxLabel: number): string => {
    // beyond the label table (tennis: owing more than 40) count on in tens: "Owe 50", "Owe 60"
    if (raw < 0) {
        const owe = -raw
        if (owe <= maxLabel) return (map.oweLabel ?? 'Owe ') + map.labels[owe]!
        const top = Number(map.labels[maxLabel])
        return (map.oweLabel ?? 'Owe ') + (Number.isFinite(top) ? String(top + 10 * (owe - maxLabel)) : map.labels[maxLabel]!)
    }
    return map.labels[Math.min(raw, maxLabel)]!
}

/**
 * @param initial handicap presets (per-unit starts, per-ordinal extra, countdown overrides);
 *                null/undefined = level, the engine runs exactly as before
 */
export function replayScoreEvents(
    schema: ScoreSchema,
    events: ScoreEvent[],
    initial?: EngineInitialState | null,
): LiveScoreState {
    const ordered = [...events].sort((a, b) =>
        a.sequence !== b.sequence ? a.sequence - b.sequence : a.clientEventId.localeCompare(b.clientEventId),
    )
    const undone = resolveUndoneEvents(ordered)
    const ctx = new ReplayContext(schema, initial ?? null)

    for (const evt of ordered) {
        if (evt.type === 'undo' || undone.has(evt.clientEventId)) continue
        ctx.apply(evt)
        ctx.state.lastAppliedSequence = Math.max(ctx.state.lastAppliedSequence, evt.sequence)
    }

    ctx.snapshot()
    return ctx.state
}

function resolveUndoneEvents(ordered: ScoreEvent[]): Set<string> {
    const undoneBy = new Map<string, string[]>()
    for (const evt of ordered) {
        if (evt.type === 'undo' && evt.undoesClientEventId) {
            const list = undoneBy.get(evt.undoesClientEventId) ?? []
            list.push(evt.clientEventId)
            undoneBy.set(evt.undoesClientEventId, list)
        }
    }

    const isUndone = (id: string, visiting: Set<string>): boolean => {
        const undos = undoneBy.get(id)
        if (!undos) return false
        for (const undoId of undos) {
            if (visiting.has(undoId)) continue
            visiting.add(undoId)
            if (!isUndone(undoId, visiting)) return true
        }
        return false
    }

    const result = new Set<string>()
    for (const evt of ordered) {
        if (evt.type === 'undo') continue
        if (isUndone(evt.clientEventId, new Set())) result.add(evt.clientEventId)
    }
    return result
}

class ReplayContext {
    readonly state: LiveScoreState
    private readonly schema: ScoreSchema
    private readonly path: ActiveUnit[] = []
    private readonly completed = new Map<string, UnitScore[]>()
    private readonly substituteOf = new Map<string, string>()

    private roleAssigned: boolean
    private holder = 'home'
    private serverNumber = 1

    private homeRemaining = 0
    private awayRemaining = 0
    private homeDarts = 0
    private awayDarts = 0
    private currentBreak = 0
    private breakSide: string | null = null

    // snooker rules state (accumulator counters with snookerRules)
    private static readonly coloursOrder = [2, 3, 4, 5, 6, 7]
    private redsRemaining = 0
    private coloursIndex = -1 // -1 = colours sequence not started
    private lastPotWasRed = false

    private eventSequence = 0

    // handicap presets + how many instances of each unit have started (pattern ordinal)
    private readonly initial: EngineInitialState | null
    private readonly unitStartCount = new Map<string, number>()

    constructor(schema: ScoreSchema, initial: EngineInitialState | null = null) {
        this.schema = schema
        this.initial = initial
        for (const [, unit] of Object.entries(schema.units)) {
            if (unit.tiebreak && unit.child) this.substituteOf.set(unit.tiebreak.replaceChildWith, unit.child)
        }

        this.state = {
            setupComplete: !schema.turns || schema.turns.initial !== 'chooseAtStart',
            setupRole: schema.turns?.roleLabel ?? schema.turns?.role ?? null,
            completed: false,
            winnerSide: null,
            resolvedResultKey: null,
            path: [],
            completedUnits: {},
            currentTurnSide: null,
            currentServerNumber: null,
            counters: {},
            events: [],
            history: [],
            games: [],
            lastAppliedSequence: 0,
            warnings: [],
        }
        this.roleAssigned = this.state.setupComplete
        if (initial) {
            const named = [
                ...Object.keys(initial.unitStarts ?? {}),
                ...(initial.unitStartPattern ?? []).map((p) => p.unit),
            ]
            for (const key of named) {
                if (!schema.units[key]) this.warn(`Handicap start names unknown unit '${key}'`)
            }
            const mappingUnit = schema.resultMapping?.unit
            if (mappingUnit && mappingUnit !== schema.rootUnit && initial.unitStarts?.[mappingUnit]) {
                this.warn(`Handicap child-win start on '${mappingUnit}' is not counted by the result mapping`)
            }
        }
        this.startUnit(schema.rootUnit, 1)
    }

    private get leaf(): ActiveUnit {
        return this.path[this.path.length - 1]!
    }

    apply(evt: ScoreEvent): void {
        const payload = evt.payload ?? null
        if (evt.type === 'setup' || evt.type === 'correction') {
            this.applySetup(payload, evt.type)
            return
        }
        if (evt.type === 'event') {
            this.applyAnnotation(evt, payload)
            return
        }

        if (this.state.completed) {
            this.warn(`event ${evt.sequence} (${evt.type}) ignored: match already complete`)
            return
        }
        if (!this.roleAssigned) {
            this.warn(`event ${evt.sequence} (${evt.type}) ignored: setup (initial ${this.state.setupRole}) not chosen yet`)
            return
        }

        switch (evt.type) {
            case 'rally':
                this.applyRally(evt, payload)
                break
            case 'point':
                this.applyPoint(evt, payload)
                break
            case 'visit':
                this.applyVisit(evt, payload)
                break
            case 'pot':
                this.applyPot(evt, payload)
                break
            case 'foul':
                this.applyFoul(evt, payload)
                break
            case 'miss':
                this.applyMiss(evt, payload)
                break
            case 'endScore':
                this.applyEndScore(evt, payload)
                break
            case 'unitEnd':
                this.applyUnitEnd(payload)
                break
            default:
                this.warn(`event ${evt.sequence} has unknown type '${evt.type}'`)
        }
    }

    private applySetup(payload: Record<string, unknown> | null, type: string): void {
        const side = getString(payload, 'side')
        if (side !== 'home' && side !== 'away') {
            this.warn(`${type} event requires side home|away`)
            return
        }

        this.holder = side
        this.roleAssigned = true
        this.state.setupComplete = true
        const serverNumber = getNumber(payload, 'serverNumber')
        if (serverNumber != null) this.serverNumber = serverNumber
        else if (type === 'setup') this.serverNumber = this.initialServerNumber()
        for (const unit of this.path) unit.holderAtStart = this.holder
    }

    // Pickleball: the game-starting side only gets one server turn, i.e. it starts on its
    // LAST server number so the first rally loss is a side-out.
    private initialServerNumber(): number {
        const rotation = this.activeRotation()
        return rotation?.type === 'sideOut' && (rotation.serversPerSide ?? 1) > 1 ? rotation.serversPerSide! : 1
    }

    private applyRally(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const wonBy = getString(payload, 'wonBy') ?? getString(payload, 'side')
        if (wonBy !== 'home' && wonBy !== 'away') {
            this.warn(`rally event ${evt.sequence} requires wonBy home|away`)
            return
        }

        const rotation = this.activeRotation()
        if (rotation?.type === 'sideOut') {
            if (wonBy === this.holder) {
                this.addPointToLeaf(wonBy, 1, evt)
            } else {
                const serversPerSide = rotation.serversPerSide ?? 1
                if (this.serverNumber < serversPerSide) {
                    this.serverNumber++
                } else {
                    this.holder = opposite(this.holder)
                    this.serverNumber = 1
                }
            }
            return
        }

        this.addPointToLeaf(wonBy, 1, evt)
    }

    private applyPoint(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const side = getString(payload, 'side')
        if (side !== 'home' && side !== 'away') {
            this.warn(`point event ${evt.sequence} requires side home|away`)
            return
        }

        const value = getNumber(payload, 'value') ?? 1
        if (this.leaf.unit.leafIncrement?.oneSidePerUnit) {
            this.completeEndScore(side, value, evt, payload)
            return
        }
        this.addPointToLeaf(side, value, evt)
    }

    private addPointToLeaf(side: string, value: number, evt: ScoreEvent): void {
        const leaf = this.leaf
        if (leaf.unit.scoredBy) {
            this.warn(`point/rally event ${evt.sequence} invalid: leaf '${leaf.name}' is counter-scored`)
            return
        }
        if (leaf.unit.leafIncrement && !leaf.unit.leafIncrement.values.includes(value))
            this.warn(`point value ${value} not in allowed increments for '${leaf.name}'`)

        if (side === 'home') leaf.home += value
        else leaf.away += value

        if (this.activeRotation()?.type === 'rallyWinner') this.holder = side
        else this.advanceTurnAfterPoint(leaf)

        const winner = this.leafWinner(leaf)
        if (winner) this.completeUnit(winner, false)
    }

    // --- countdown counter (darts X01) ---

    private applyVisit(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const leaf = this.leaf
        const counter = this.getCounter(leaf, 'countdown')
        if (!counter) {
            this.warn(`visit event ${evt.sequence} invalid: leaf '${leaf.name}' has no countdown counter`)
            return
        }

        const side = getString(payload, 'side')
        const total = getNumber(payload, 'total')
        if ((side !== 'home' && side !== 'away') || total == null) {
            this.warn(`visit event ${evt.sequence} requires side and total`)
            return
        }

        const darts = getNumber(payload, 'darts') ?? counter.perTurnDarts ?? 3
        if (side === 'home') this.homeDarts += darts
        else this.awayDarts += darts

        const playerId = getString(payload, 'playerId')
        this.evaluateAutoDetect('visitTotalEquals', total, side, playerId, evt, (t) => total === t)
        this.evaluateAutoDetect('visitTotalAtLeast', total, side, playerId, evt, (t) => total >= t)

        const remaining = side === 'home' ? this.homeRemaining : this.awayRemaining
        const next = remaining - total
        const minFinishFrom = counter.finish?.min ?? 2

        if (next < 0 || (next > 0 && next < minFinishFrom)) {
            // bust: visit reverts, turn still passes
            this.holder = opposite(this.holder)
            return
        }

        if (next === 0) {
            this.evaluateAutoDetect('checkoutAtLeast', total, side, playerId, evt, (t) => total >= t)
            const winnerDarts = side === 'home' ? this.homeDarts : this.awayDarts
            this.evaluateAutoDetect('dartsInLeg', winnerDarts, side, playerId, evt, () => true)
            this.evaluateAutoDetect('dartsInLegEquals', winnerDarts, side, playerId, evt, (t) => winnerDarts === t)

            leaf.home = side === 'home' ? 1 : 0
            leaf.away = side === 'away' ? 1 : 0
            this.completeUnit(side, false)
            return
        }

        if (side === 'home') this.homeRemaining = next
        else this.awayRemaining = next
        this.holder = opposite(this.holder)
    }

    // --- accumulator counter (snooker break) ---

    private applyPot(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const leaf = this.leaf
        const counter = this.getCounter(leaf, 'accumulator')
        if (!counter) {
            this.warn(`pot event ${evt.sequence} invalid: leaf '${leaf.name}' has no accumulator counter`)
            return
        }

        const side = getString(payload, 'side')
        const value = getNumber(payload, 'value')
        if ((side !== 'home' && side !== 'away') || value == null) {
            this.warn(`pot event ${evt.sequence} requires side and value`)
            return
        }
        if (counter.values && !counter.values.includes(value))
            this.warn(`pot value ${value} not allowed for '${leaf.name}'`)

        if (this.breakSide !== side) {
            this.endBreak(evt)
            if (counter.snookerRules) {
                // a new break starts fresh: incoming player is on red, or on the
                // colours sequence once the reds (and choice colour) are gone
                this.lastPotWasRed = false
                if (this.redsRemaining === 0 && this.coloursIndex === -1) this.coloursIndex = 0
            }
        }

        if (counter.snookerRules) {
            const allowed = this.snookerAllowedValues(counter)
            if (allowed && !allowed.includes(value))
                this.warn(`pot ${value} out of sequence for '${leaf.name}'`) // UI blocks; replay stays tolerant
        }

        this.breakSide = side
        this.currentBreak += value
        this.holder = side

        if (side === 'home') leaf.home += value
        else leaf.away += value

        if (counter.snookerRules) {
            this.advanceSnookerSequence(value)
            // black potted at the end of the colours sequence: the frame is over
            if (this.redsRemaining === 0 && this.coloursIndex >= ReplayContext.coloursOrder.length) {
                this.endBreak(evt)
                const winner = leaf.home === leaf.away ? null : leaf.home > leaf.away ? 'home' : 'away'
                this.completeUnit(winner, winner === null)
            }
        }
    }

    private advanceSnookerSequence(value: number): void {
        if (value === 1) {
            if (this.redsRemaining > 0) this.redsRemaining--
            this.lastPotWasRed = true
            return
        }

        if (this.redsRemaining > 0) {
            this.lastPotWasRed = false // colour re-spots while reds remain
        } else if (this.coloursIndex === -1) {
            // the colour of choice after the final red — the yellow-to-black
            // sequence starts with the next pot
            this.coloursIndex = 0
            this.lastPotWasRed = false
        } else if (this.coloursIndex < ReplayContext.coloursOrder.length) {
            this.coloursIndex++
        }
    }

    // Which balls are legal to pot right now; null when the counter has no snooker rules.
    private snookerAllowedValues(counter: { snookerRules?: { reds: number } | null }): number[] | null {
        if (!counter.snookerRules) return null
        if (this.redsRemaining > 0 || (this.coloursIndex === -1 && this.lastPotWasRed))
            return this.lastPotWasRed ? [...ReplayContext.coloursOrder] : [1]
        const index = Math.max(this.coloursIndex, 0)
        return index < ReplayContext.coloursOrder.length ? [ReplayContext.coloursOrder[index]!] : []
    }

    private applyFoul(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const leaf = this.leaf
        const counter = this.getCounter(leaf, 'accumulator')
        if (!counter) {
            this.warn(`foul event ${evt.sequence} invalid: leaf '${leaf.name}' has no accumulator counter`)
            return
        }

        const side = getString(payload, 'side')
        const value = getNumber(payload, 'value')
        if ((side !== 'home' && side !== 'away') || value == null) {
            this.warn(`foul event ${evt.sequence} requires side and value`)
            return
        }
        if (counter.foul && !counter.foul.awardOpponent.includes(value))
            this.warn(`foul value ${value} not allowed for '${leaf.name}'`)

        this.endBreak(evt)
        if (counter.snookerRules) {
            // the incoming player restarts the alternation (or the colours sequence)
            this.lastPotWasRed = false
            if (this.redsRemaining === 0 && this.coloursIndex === -1) this.coloursIndex = 0
        }

        const beneficiary = opposite(side)
        if (beneficiary === 'home') leaf.home += value
        else leaf.away += value
        this.holder = beneficiary
        this.state.history.push({ type: 'foul', side, value, unitRef: this.currentUnitRef() })
    }

    // A miss (failed pot, no foul): the break ends and the table passes to the opponent.
    private applyMiss(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const leaf = this.leaf
        const counter = this.getCounter(leaf, 'accumulator')
        if (!counter) {
            this.warn(`miss event ${evt.sequence} invalid: leaf '${leaf.name}' has no accumulator counter`)
            return
        }

        const side = getString(payload, 'side') ?? this.breakSide ?? this.holder
        if (side !== 'home' && side !== 'away') {
            this.warn(`miss event ${evt.sequence} requires side home|away`)
            return
        }

        this.endBreak(evt)
        if (counter.snookerRules) {
            // the incoming player is on red (or the colours sequence once reds are gone)
            this.lastPotWasRed = false
            if (this.redsRemaining === 0 && this.coloursIndex === -1) this.coloursIndex = 0
        }

        this.holder = opposite(side)
    }

    private endBreak(evt: ScoreEvent): void {
        if (this.breakSide && this.currentBreak > 0) {
            const side = this.breakSide
            const breakValue = this.currentBreak
            this.evaluateAutoDetect('breakAtLeast', breakValue, side, null, evt, (t) => breakValue >= t)
            this.state.history.push({ type: 'break', side, value: breakValue, unitRef: this.currentUnitRef() })
        }
        this.currentBreak = 0
        this.breakSide = null
    }

    // --- one-side leaves (bowls ends) ---

    private applyEndScore(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const side = getString(payload, 'side') // null = dead/drawn end
        const value = getNumber(payload, 'value') ?? 0
        if (side !== null && side !== 'home' && side !== 'away') {
            this.warn(`endScore event ${evt.sequence} has invalid side`)
            return
        }
        this.completeEndScore(side, value, evt, payload)
    }

    private completeEndScore(
        side: string | null,
        value: number,
        evt: ScoreEvent,
        payload: Record<string, unknown> | null,
    ): void {
        const leaf = this.leaf
        if (leaf.unit.leafIncrement && side !== null && !leaf.unit.leafIncrement.values.includes(value))
            this.warn(`end score ${value} not in allowed values for '${leaf.name}'`)

        leaf.home = side === 'home' ? value : 0
        leaf.away = side === 'away' ? value : 0

        if (side !== null)
            this.evaluateAutoDetect('endShotsAtLeast', value, side, getString(payload, 'playerId'), evt, (t) => value >= t)

        this.completeUnit(side, side === null)
    }

    private applyUnitEnd(payload: Record<string, unknown> | null): void {
        const side = getString(payload, 'side')
        if (this.leaf.unit.scoredBy) {
            this.endBreak({ clientEventId: '', sequence: this.state.lastAppliedSequence, type: 'unitEnd' })
        }

        const winner =
            side === 'home' || side === 'away'
                ? side
                : this.leaf.home === this.leaf.away
                  ? null
                  : this.leaf.home > this.leaf.away
                    ? 'home'
                    : 'away'
        this.completeUnit(winner, winner === null)
    }

    // --- annotation events ---

    private applyAnnotation(evt: ScoreEvent, payload: Record<string, unknown> | null): void {
        const key = getString(payload, 'key')
        if (!key) {
            this.warn(`event ${evt.sequence} requires key`)
            return
        }
        const def = this.schema.events.find((e) => e.key === key)
        if (!def) {
            this.warn(`event ${evt.sequence} has unknown key '${key}'`)
            return
        }

        const side = getString(payload, 'side')
        const playerId = getString(payload, 'playerId')
        if (def.playerRequired && !playerId) this.warn(`event '${key}' expects a player`)

        this.recordEvent({
            key,
            side,
            playerId,
            value: getNumber(payload, 'value') ?? def.value ?? null,
            metadata: payload?.metadata ?? null,
            unitRef: this.currentUnitRef(),
            occurredAt: evt.occurredAt ?? null,
            sequence: 0,
            auto: false,
        })

        if (def.affectsScore && (side === 'home' || side === 'away') && !this.state.completed) {
            const target = [...this.path].reverse().find((u) => u.name === def.affectsScore!.unit)
            if (!target) {
                this.warn(`event '${key}' affectsScore unit '${def.affectsScore.unit}' is not active`)
                return
            }
            // opponent: the event is recorded against the side it happened TO
            // (an own goal attributes the player on the conceding team) while
            // the score lands with the other side.
            const scoreSide = def.affectsScore.opponent ? (side === 'home' ? 'away' : 'home') : side
            if (target === this.leaf) {
                this.addPointToLeaf(scoreSide, def.affectsScore.increment, evt)
            } else if (scoreSide === 'home') {
                target.home += def.affectsScore.increment
            } else {
                target.away += def.affectsScore.increment
            }
        }
    }

    // --- unit lifecycle ---

    private startUnit(name: string, index: number): void {
        const unit = this.schema.units[name]!
        const active: ActiveUnit = {
            name, unit, index, home: 0, away: 0, startHome: 0, startAway: 0, childrenCompleted: 0, holderAtStart: this.holder,
        }
        this.path.push(active)

        // handicap presets: every instance of the named unit begins from the start (points on a
        // leaf, child wins on a container), plus the per-ordinal extra counted match-wide
        if (this.initial) {
            // a set starting restarts the six-game cycle for its games (pattern.resetOn)
            for (const pattern of this.initial.unitStartPattern ?? []) {
                if (pattern.resetOn === name) this.unitStartCount.set(pattern.unit, 0)
            }
            const ordinal = (this.unitStartCount.get(name) ?? 0) + 1
            this.unitStartCount.set(name, ordinal)
            const starts = this.initial.unitStarts?.[name]
            if (starts && starts.length >= 2) {
                active.home = starts[0]!
                active.away = starts[1]!
            }
            for (const pattern of this.initial.unitStartPattern ?? []) {
                if (pattern.unit !== name || pattern.everyN <= 0 || pattern.extra.length < 2) continue
                const position = ((ordinal - 1) % pattern.everyN) + 1
                const applies = pattern.slots && pattern.slots.length ? pattern.slots.includes(position) : position <= pattern.extraInFirstK
                if (applies) {
                    active.home += pattern.extra[0]!
                    active.away += pattern.extra[1]!
                }
            }
            active.startHome = active.home
            active.startAway = active.away
        }

        if (unit.scoredBy) {
            const counter = this.schema.counters[unit.scoredBy]
            if (counter?.type === 'countdown') {
                this.homeRemaining = counter.start ?? 0
                this.awayRemaining = counter.start ?? 0
                const counterStart = this.initial?.counterStarts?.[unit.scoredBy]
                if (counterStart) {
                    this.homeRemaining = counterStart.home
                    this.awayRemaining = counterStart.away
                }
                this.homeDarts = 0
                this.awayDarts = 0
            }
            this.currentBreak = 0
            this.breakSide = null
            this.redsRemaining = counter?.snookerRules?.reds ?? 0
            this.coloursIndex = -1
            this.lastPotWasRed = false
        }

        if (unit.child) this.startUnit(this.effectiveChild(this.path[this.path.length - 1]!), 1)
    }

    private effectiveChild(parent: ActiveUnit): string {
        const unit = parent.unit
        if (
            unit.tiebreak &&
            unit.tiebreak.atScore.length === 2 &&
            parent.home === unit.tiebreak.atScore[0] &&
            parent.away === unit.tiebreak.atScore[1]
        )
            return unit.tiebreak.replaceChildWith
        return unit.child!
    }

    private completeUnit(winner: string | null, isDraw: boolean): void {
        let completing = this.leaf
        this.recordCompleted(completing, isDraw ? null : winner)
        this.emitUnitCompletionAutoDetect(completing, isDraw ? null : winner)
        this.handleTurnOnUnitCompletion(completing, isDraw ? null : winner)
        this.path.pop()

        while (this.path.length > 0) {
            const parent = this.leaf
            parent.childrenCompleted++
            this.applyChildResult(parent, completing, winner)

            const { winner: parentWinner, draw: parentDraw, done } = this.containerWinner(parent)
            if (!done) {
                this.startUnit(this.effectiveChild(parent), parent.childrenCompleted + 1)
                return
            }

            this.recordCompleted(parent, parentDraw ? null : parentWinner)
            this.emitUnitCompletionAutoDetect(parent, parentDraw ? null : parentWinner)
            this.handleTurnOnUnitCompletion(parent, parentDraw ? null : parentWinner)
            completing = parent
            winner = parentDraw ? null : parentWinner
            this.path.pop()
        }

        this.state.completed = true
        this.state.winnerSide = winner
        this.resolveResult(completing, winner)
    }

    private applyChildResult(parent: ActiveUnit, child: ActiveUnit, winner: string | null): void {
        if (parent.unit.winCondition.type === 'aggregate') {
            parent.home += child.home
            parent.away += child.away
        } else if (winner === 'home') {
            parent.home++
        } else if (winner === 'away') {
            parent.away++
        }
    }

    private leafWinner(leaf: ActiveUnit): string | null {
        const wc = leaf.unit.winCondition
        if (wc.type !== 'target') return null

        const target = wc.target ?? 1
        let winBy = wc.winBy ?? 1

        const displayMap = this.getDisplayMap(leaf.unit)
        if (displayMap?.goldenPoint && leaf.home >= target - 1 && leaf.away >= target - 1) winBy = 1

        for (const [score, other, side] of [
            [leaf.home, leaf.away, 'home'],
            [leaf.away, leaf.home, 'away'],
        ] as const) {
            if (wc.cap != null && score >= wc.cap) return side
            if (score >= target && score - other >= winBy) return side
        }
        return null
    }

    private containerWinner(container: ActiveUnit): { winner: string | null; draw: boolean; done: boolean } {
        const wc = container.unit.winCondition
        switch (wc.type) {
            case 'bestOf': {
                const needed = Math.floor(wc.bestOf! / 2) + 1
                if (container.home >= needed) return { winner: 'home', draw: false, done: true }
                if (container.away >= needed) return { winner: 'away', draw: false, done: true }
                return { winner: null, draw: false, done: false }
            }
            case 'target': {
                const target = wc.target ?? 1
                const winBy = wc.winBy ?? 1
                for (const [score, other, side] of [
                    [container.home, container.away, 'home'],
                    [container.away, container.home, 'away'],
                ] as const) {
                    if ((wc.cap != null && score >= wc.cap) || (score >= target && score - other >= winBy))
                        return { winner: side, draw: false, done: true }
                }
                return { winner: null, draw: false, done: false }
            }
            case 'fixedCount':
            case 'aggregate': {
                // aggregate-until-target (bowls singles: first to 21 shots, open-ended
                // ends) completes as soon as a side reaches the target
                if (wc.type === 'aggregate' && wc.target != null) {
                    if (container.home >= wc.target) return { winner: 'home', draw: false, done: true }
                    if (container.away >= wc.target) return { winner: 'away', draw: false, done: true }
                    if (wc.count == null) return { winner: null, draw: false, done: false }
                }

                if (container.childrenCompleted < wc.count!) return { winner: null, draw: false, done: false }
                if (container.home > container.away) return { winner: 'home', draw: false, done: true }
                if (container.away > container.home) return { winner: 'away', draw: false, done: true }
                return { winner: null, draw: true, done: true }
            }
            default:
                return { winner: null, draw: false, done: false }
        }
    }

    private recordCompleted(unit: ActiveUnit, winner: string | null): void {
        const list = this.completed.get(unit.name) ?? []
        list.push({ unit: unit.name, index: unit.index, home: unit.home, away: unit.away, winnerSide: winner })
        this.completed.set(unit.name, list)
    }

    // --- turn tracking ---

    private activeRotation(): SchemaTurnRotation | null {
        return this.leaf?.unit.turnRotation ?? this.schema.turns?.rotation ?? null
    }

    private advanceTurnAfterPoint(leaf: ActiveUnit): void {
        const rotation = this.activeRotation()
        if (!rotation || !this.schema.turns) return
        const rotatesWithinLeaf = rotation.type === 'everyNPoints' || rotation.firstServeSinglePoint === true
        if (!rotatesWithinLeaf) return

        // points PLAYED, not the score: a handicapped game starting 2–0 has had no rallies yet
        const points = (leaf.home - leaf.startHome) + (leaf.away - leaf.startAway)
        const flipsBefore = this.rotationFlips(points - 1, rotation, leaf)
        const flipsAfter = this.rotationFlips(points, rotation, leaf)
        if (flipsAfter > flipsBefore) this.holder = opposite(this.holder)
    }

    private rotationFlips(points: number, rotation: SchemaTurnRotation, leaf: ActiveUnit): number {
        if (points <= 0) return 0
        const n = rotation.n ?? 1

        // tiebreak pattern: one point on first serve, then every n — flips = ceil(points / n)
        if (rotation.firstServeSinglePoint) return Math.ceil(points / n)

        let threshold = Number.MAX_SAFE_INTEGER
        if (rotation.deuceOverride && rotation.deuceOverride.afterScore.length === 2) {
            if (
                leaf.home >= rotation.deuceOverride.afterScore[0]! &&
                leaf.away >= rotation.deuceOverride.afterScore[1]!
            )
                threshold = rotation.deuceOverride.afterScore[0]! + rotation.deuceOverride.afterScore[1]!
        }

        if (points <= threshold) return Math.floor(points / n)
        return Math.floor(threshold / n) + Math.floor((points - threshold) / rotation.deuceOverride!.n)
    }

    private handleTurnOnUnitCompletion(completedUnit: ActiveUnit, winner: string | null): void {
        const turns = this.schema.turns
        if (!turns) return
        const rotation = turns.rotation
        const boundary = rotation.unit
        if (!boundary) return

        const isBoundary =
            completedUnit.name === boundary || this.substituteOf.get(completedUnit.name) === boundary
        if (!isBoundary) return

        switch (rotation.type) {
            case 'winnerHolds':
            case 'rallyWinner': // unit winner serves first in the next unit
                if (winner) this.holder = winner
                break
            case 'sideOut':
                this.holder = opposite(completedUnit.holderAtStart)
                this.serverNumber = rotation.serversPerSide ?? 1
                break
            default:
                this.holder = opposite(completedUnit.holderAtStart)
        }
    }

    // --- result resolution ---

    private resolveResult(root: ActiveUnit, winner: string | null): void {
        const mapping = this.schema.resultMapping
        let entry = null

        if (mapping.type === 'childUnitsWon') {
            let home: number
            let away: number
            const unitName = mapping.unit ?? this.schema.rootUnit
            if (unitName === root.name) {
                home = root.home
                away = root.away
            } else {
                const completed = this.completed.get(unitName) ?? []
                home = completed.filter((u) => u.winnerSide === 'home').length
                away = completed.filter((u) => u.winnerSide === 'away').length
            }
            entry = mapping.map.find((m) => m.home === home && m.away === away) ?? null
            if (!entry) this.warn(`no resultMapping entry for ${home}-${away}`)
        } else if (mapping.type === 'winLoss') {
            const outcome = winner === 'home' ? 'homeWin' : winner === 'away' ? 'awayWin' : 'draw'
            entry = mapping.map.find((m) => m.outcome === outcome) ?? null
            if (!entry) this.warn(`no resultMapping entry for outcome '${outcome}'`)
        }

        this.state.resolvedResultKey = entry?.resultKey ?? null

        if (mapping.aggregateToGames) {
            this.state.games.push({ home: root.home, away: root.away, sort: 1 })
        } else if (mapping.gamesFrom) {
            const source: UnitScore[] = [...(this.completed.get(mapping.gamesFrom) ?? [])]
            for (const [substitute, replaced] of this.substituteOf) {
                if (replaced === mapping.gamesFrom) source.push(...(this.completed.get(substitute) ?? []))
            }
            let sort = 1
            for (const unit of source) this.state.games.push({ home: unit.home, away: unit.away, sort: sort++ })
        }
    }

    // --- auto-detect + explicit events ---

    private evaluateAutoDetect(
        rule: string,
        actualValue: number,
        side: string | null,
        playerId: string | null,
        evt: ScoreEvent,
        predicate: (threshold: number) => boolean,
    ): void {
        for (const def of this.schema.events) {
            const raw = def.autoDetect?.[rule]
            if (raw === undefined) continue
            const threshold = typeof raw === 'number' ? raw : null
            const applies = threshold != null ? predicate(threshold) : predicate(0)
            if (!applies) continue

            this.recordEvent({
                key: def.key,
                side,
                playerId,
                value: actualValue,
                unitRef: this.currentUnitRef(),
                occurredAt: evt.occurredAt ?? null,
                sequence: 0,
                auto: true,
            })
        }
    }

    private emitUnitCompletionAutoDetect(unit: ActiveUnit, winner: string | null): void {
        for (const def of this.schema.events) {
            if (!def.autoDetect) continue

            const shutoutUnit = def.autoDetect['unitShutout']
            if (
                typeof shutoutUnit === 'string' &&
                shutoutUnit === unit.name &&
                winner &&
                (winner === 'home' ? unit.away : unit.home) === 0
            ) {
                this.recordEvent({
                    key: def.key,
                    side: winner,
                    unitRef: this.currentUnitRef(),
                    auto: true,
                    value: Math.max(unit.home, unit.away),
                    sequence: 0,
                })
            }

            const deuceUnit = def.autoDetect['deuceGame']
            const target = unit.unit.winCondition.target
            if (
                typeof deuceUnit === 'string' &&
                deuceUnit === unit.name &&
                target != null &&
                Math.min(unit.home, unit.away) >= target - 1
            ) {
                this.recordEvent({ key: def.key, side: winner, unitRef: this.currentUnitRef(), auto: true, sequence: 0 })
            }
        }
    }

    private recordEvent(detected: DetectedEvent): void {
        detected.sequence = ++this.eventSequence
        this.state.events.push(detected)
    }

    private currentUnitRef(): string {
        return this.path
            .slice(1)
            .map((u) => `${u.name}:${u.index}`)
            .join('/')
    }

    // --- projection ---

    snapshot(): void {
        // A sport with neither a turns config NOR a counter-scored unit
        // (snooker's break tracks the table through scoredBy) has no turn/serve
        // concept at all (football, basketball) — never surface a holder for
        // the UI to highlight.
        const hasTurnConcept =
            !!this.schema.turns || Object.values(this.schema.units).some((u) => u.scoredBy)
        this.state.currentTurnSide =
            this.state.completed || !this.roleAssigned || !hasTurnConcept ? null : this.holder
        const rotation = this.path.length > 0 ? this.activeRotation() : (this.schema.turns?.rotation ?? null)
        this.state.currentServerNumber =
            rotation?.type === 'sideOut' && !this.state.completed ? this.serverNumber : null

        for (const unit of this.path) {
            const score: UnitScore = { unit: unit.name, index: unit.index, home: unit.home, away: unit.away }
            this.applyDisplay(unit, score)
            this.state.path.push(score)
        }

        for (const [name, list] of this.completed) this.state.completedUnits[name] = list

        if (this.path.length > 0 && this.leaf.unit.scoredBy) {
            const counter = this.schema.counters[this.leaf.unit.scoredBy]
            if (counter) {
                this.state.counters[this.leaf.unit.scoredBy] =
                    counter.type === 'countdown'
                        ? ({
                              homeRemaining: this.homeRemaining,
                              awayRemaining: this.awayRemaining,
                              homeDartsThrown: this.homeDarts,
                              awayDartsThrown: this.awayDarts,
                          } satisfies CounterState)
                        : ({
                              currentBreak: this.currentBreak,
                              currentBreakSide: this.breakSide,
                              allowedValues: this.snookerAllowedValues(counter),
                              redsRemaining: counter.snookerRules ? this.redsRemaining : null,
                          } satisfies CounterState)
            }
        }
    }

    private getDisplayMap(unit: SchemaUnit): SchemaDisplayMap | null {
        const display = unit.scoreDisplay ?? 'perSide'
        if (display === 'perSide' || display === 'aggregate') return null
        return this.schema.displayMaps[display] ?? null
    }

    private applyDisplay(unit: ActiveUnit, score: UnitScore): void {
        const map = this.getDisplayMap(unit.unit)
        if (!map || map.labels.length === 0) return

        const maxLabel = map.labels.length - 1
        if (unit.home >= maxLabel && unit.away >= maxLabel) {
            if (unit.home === unit.away) {
                const deuce = map.goldenPoint ? (map.goldenPointLabel ?? 'Golden point') : (map.deuceLabel ?? 'Deuce')
                score.homeDisplay = map.labels[maxLabel]!
                score.awayDisplay = map.labels[maxLabel]!
                score.statusDisplay = deuce
            } else {
                const advSide = unit.home > unit.away ? 'home' : 'away'
                const advLabel = map.advLabel ?? 'Adv'
                score.homeDisplay = advSide === 'home' ? advLabel : map.labels[maxLabel]!
                score.awayDisplay = advSide === 'away' ? advLabel : map.labels[maxLabel]!
            }
            return
        }

        score.homeDisplay = mappedLabel(map, unit.home, maxLabel)
        score.awayDisplay = mappedLabel(map, unit.away, maxLabel)
    }

    private getCounter(leaf: ActiveUnit, type: string) {
        if (!leaf.unit.scoredBy) return null
        const counter = this.schema.counters[leaf.unit.scoredBy]
        return counter?.type === type ? counter : null
    }

    private warn(message: string): void {
        this.state.warnings.push(message)
    }
}

function getString(payload: Record<string, unknown> | null, key: string): string | null {
    const value = payload?.[key]
    return typeof value === 'string' ? value : null
}

function getNumber(payload: Record<string, unknown> | null, key: string): number | null {
    const value = payload?.[key]
    return typeof value === 'number' ? value : null
}
